import { Injectable, StateKey, inject, makeStateKey } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { ContentfulPage, PageComponents } from 'src/app/core/interfaces';
import { PageLayout } from 'src/app/layout/layout.enum';
import { ServerNavigateService } from 'src/app/shared/services/server-navigate.service';
import { GenericLogger, PageResolver } from '@scalefast/ecommerce-core';
import { ContentfulConfigService } from '@scalefast/config-angular';
import { CUSTOM_PAGE_LAYOUT_TYPES } from 'src/app/core/interfaces/page-creator';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';
import { ContentfulPagePathContent } from 'src/app/core/interfaces/Contentful/PagePath/ContentfulPagePathContent';
import {
  getContentfulConfig,
  isAEmptyContentfulPagePathContent,
  isAnErrorContentfulPagePathContent,
} from 'src/app/shared/helpers/contentful.helper';
import { ContentfulLiteService } from 'src/app/shared/services/contentful-lite.service';
import { ContentfulPrefetchService } from 'src/app/shared/services/contentful-prefetch.service';

@Injectable({ providedIn: 'root' })
export class PageContentfulResolver implements PageResolver {
  private logger = inject(GenericLogger);
  private router = inject(Router);
  #contentfulLiteService = inject(ContentfulLiteService);
  private serverNavigateService = inject(ServerNavigateService);
  private contentfulConfigService = inject(ContentfulConfigService);
  private contentfulLocale: string;
  #contentfulPrefetchService = inject(ContentfulPrefetchService);

  constructor() {
    const contentfulConfig = getContentfulConfig(this.contentfulConfigService);
    this.contentfulLocale = contentfulConfig.locale;
  }

  async resolve(route: ActivatedRouteSnapshot): Promise<ContentfulPage | null> {
    const slug = this.prepareSlug(route);
    const prefetchedContent = await this.#contentfulPrefetchService.getPrefetchedPage(slug);
    if (prefetchedContent) {
      return this.getContentfulPageFromPath(prefetchedContent, route);
    }
    const contentfulPagePathContent = await this.#contentfulLiteService.resolveContentfulPageByPath(slug);
    if (!this.isAContentfulPagePathContentValid(contentfulPagePathContent)) {
      return null;
    }
    // The page is reloaded when the user accesses it to avoid slow loading times. This reload causes the requested page data to be performed on the server side.
    if (this.router.navigated) {
      await this.serverNavigateService.navigateToRoute(route);
    }
    return this.getContentfulPageFromPath(contentfulPagePathContent, route);
  }

  private getContentfulPageFromPath(
    contentfulPagePathContent: ContentfulPagePathContent,
    route: ActivatedRouteSnapshot,
  ): ContentfulPage {
    const routeParams = Object.values(route.params);
    return {
      pageProvider: 'contentful',
      name: '',
      type: CUSTOM_PAGE_LAYOUT_TYPES.CONTENTFUL_PAGE,
      pageType: PageLayout.Default,
      extraData: {
        routeParams,
        contentfulPagePathContent,
        components: new PageComponents(),
      },
    } as ContentfulPage;
  }

  private isAContentfulPagePathContentValid(contentfulPagePathContent: ContentfulPagePathContent): boolean {
    return (
      !isAEmptyContentfulPagePathContent(contentfulPagePathContent) &&
      !isAnErrorContentfulPagePathContent(contentfulPagePathContent)
    );
  }

  private prepareSlug(route: ActivatedRouteSnapshot): string {
    const localeSlugs = ['en-en', 'es-es', 'fr-fr'];
    const completeRouteSlug = PageContentfulResolver.getCompleteSlug(route.params);
    let fullSlug: string;
    if (localeSlugs.includes(completeRouteSlug.split('/')[0]) || localeSlugs.includes(completeRouteSlug.split('/')[1])) {
      this.logger.warn({
        msg: `Contentful URL ${completeRouteSlug} resolved with the baseURL locale, all current production/dev modes should not have baserUrl`,
        details: {
          completeRouteSlug,
          contentfulLocale: this.contentfulLocale,
          route: {
            // We use JSON.stringify to avoid [Object object] in the logs and to have a better view of the object
            url: JSON.stringify(route.url),
            params: JSON.stringify(route.params),
            queryParams: JSON.stringify(route.queryParams),
          },
          environment: getContentfulConfig(this.contentfulConfigService),
        },
      });
    }
    // HACK: contentful Locale matches baseURL just by luck but we probably will remove this code in the future
    fullSlug = '/' + this.contentfulLocale.toLowerCase() + '/' + completeRouteSlug;
    // remove duplicated slashes
    return fullSlug.replace(/\/{2,}/g, '/');
  }

  public static getContentFullContentStateKey(routeParams: any[]): StateKey<ContentfulSectionContent[]> {
    return makeStateKey(`resolver:contentfull-${routeParams.toString()}`);
  }

  public static getCompleteSlug(routeParams: Params): string {
    let contentSlug = '';
    Object.values(routeParams).forEach((value) => {
      contentSlug = contentSlug + `/${value}`;
    });
    return contentSlug;
  }
}
